import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useExceptionsQueue,
  useApproveException,
  useRejectException,
  type ExceptionQueueFilters,
  type ExceptionQueueItem,
} from '@/hooks/useExceptions';
import { Card, CardHeader, CardTitle, CardContent, Button, Modal } from '@/components/ui';
import Badge from '@/components/ui/Badge';
import { LoadingSpinner, ErrorMessage } from '@/components/common';
import { LineItemDetail } from '@/components/charting';
import type { ExceptionStatus, BusinessCategory } from '@/types';

export default function ExceptionsQueue() {
  const [searchParams] = useSearchParams();
  
  // Get initial filter from URL params
  const urlStatus = searchParams.get('status') as ExceptionStatus | null;
  const urlView = searchParams.get('view');
  
  const [filters, setFilters] = useState<ExceptionQueueFilters>({
    exceptionStatus: (urlStatus || (urlView === 'all' ? undefined : 'in_review')) as ExceptionStatus,
    sortBy: 'expiration',
    sortOrder: 'asc',
    limit: 50,
    offset: 0,
  });
  const [selectedItem, setSelectedItem] = useState<ExceptionQueueItem | null>(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [comments, setComments] = useState('');
  const [regionInput, setRegionInput] = useState('');
  const [marketInput, setMarketInput] = useState('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedLineItemId, setSelectedLineItemId] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useExceptionsQueue(filters);
  const approveException = useApproveException();
  const rejectException = useRejectException();

  // Memoize callbacks to prevent re-renders
  const handleFilterChange = useCallback((key: keyof ExceptionQueueFilters, value: ExceptionQueueFilters[keyof ExceptionQueueFilters]) => {
    setFilters((prev) => ({ ...prev, [key]: value, offset: 0 }));
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({
      sortBy: 'expiration',
      sortOrder: 'asc',
      limit: 50,
      offset: 0,
    });
    setRegionInput('');
    setMarketInput('');
  }, []);

  const handleRegionInputChange = useCallback((value: string) => {
    setRegionInput(value);
  }, []);

  const handleMarketInputChange = useCallback((value: string) => {
    setMarketInput(value);
  }, []);

  // Debounce region filter
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (regionInput !== (filters.region || '')) {
        handleFilterChange('region', regionInput || undefined);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [regionInput, filters.region, handleFilterChange]);

  // Debounce market filter
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (marketInput !== (filters.market || '')) {
        handleFilterChange('market', marketInput || undefined);
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [marketInput, filters.market, handleFilterChange]);

  const handleOpenActionModal = (
    item: ExceptionQueueItem,
    action: 'approve' | 'reject'
  ) => {
    setSelectedItem(item);
    setActionType(action);
    setComments('');
    setShowActionModal(true);
  };

  const handleOpenDetailModal = (lineItemId: string) => {
    setSelectedLineItemId(lineItemId);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setSelectedLineItemId(null);
    setShowDetailModal(false);
  };

  const handleSubmitAction = async () => {
    if (!selectedItem || !actionType) return;

    if (!comments.trim()) {
      alert('Comments/reason is required');
      return;
    }

    try {
      if (actionType === 'approve') {
        await approveException.mutateAsync({
          lineItemId: selectedItem.id,
          comments,
        });
      } else if (actionType === 'reject') {
        await rejectException.mutateAsync({
          lineItemId: selectedItem.id,
          reason: comments,
        });
      }
      setShowActionModal(false);
      setSelectedItem(null);
      setComments('');
      refetch();
    } catch (error) {
      console.error('Failed to submit action:', error);
    }
  };

  const getStatusBadgeColor = (status: ExceptionStatus) => {
    switch (status) {
      case 'in_review':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'not_approved':
        return 'bg-red-100 text-red-800';
      case 'revision_required_by_sales':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-neutral-100 text-neutral-900';
    }
  };

  const getStatusText = (status: ExceptionStatus) => {
    switch (status) {
      case 'in_review':
        return 'In Review';
      case 'approved':
        return 'Approved';
      case 'not_approved':
        return 'Not Approved';
      case 'revision_required_by_sales':
        return 'Revision Required';
      default:
        return status;
    }
  };

  const getExpirationColor = (days: number | undefined) => {
    if (days === undefined) return 'text-neutral-600';
    if (days < 0) return 'text-red-600 font-semibold';
    if (days === 0) return 'text-orange-600 font-semibold';
    if (days <= 2) return 'text-yellow-600';
    return 'text-neutral-600';
  };

  const getBusinessCategoryBadge = (category: BusinessCategory) => {
    switch (category) {
      case 'movie_studio':
        return <Badge className="bg-purple-100 text-purple-800">Movie Studio</Badge>;
      case 'premium':
        return <Badge className="bg-outfront-purple-light text-outfront-purple">Premium</Badge>;
      default:
        return <Badge className="bg-neutral-100 text-neutral-900">Standard</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message="Failed to load exceptions queue" />
      </div>
    );
  }

  const items = data?.items || [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-neutral-900">Exceptions Review Queue</h1>
        <p className="mt-2 text-neutral-600">
          {urlView === 'all' 
            ? 'Viewing all exception requests across all statuses and regions'
            : urlStatus === 'in_review'
            ? 'Viewing exception requests pending your approval'
            : 'Review and approve exception hold requests across all regions'}
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-600 mb-1">
                Exception Status
              </label>
              <select
                value={filters.exceptionStatus || ''}
                onChange={(e) =>
                  handleFilterChange(
                    'exceptionStatus',
                    e.target.value || undefined
                  )
                }
                className="flex h-10 w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-outfront-purple focus:border-transparent"
              >
                <option value="">All Statuses</option>
                <option value="in_review">In Review</option>
                <option value="approved">Approved</option>
                <option value="not_approved">Not Approved</option>
                <option value="revision_required_by_sales">Revision Required</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-600 mb-1">
                Region
              </label>
              <input
                type="text"
                value={regionInput}
                onChange={(e) => handleRegionInputChange(e.target.value)}
                placeholder="Filter by region..."
                className="flex h-10 w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-outfront-purple focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-600 mb-1">
                Market
              </label>
              <input
                type="text"
                value={marketInput}
                onChange={(e) => handleMarketInputChange(e.target.value)}
                placeholder="Filter by market..."
                className="flex h-10 w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-outfront-purple focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-600 mb-1">
                Media Type
              </label>
              <select
                value={filters.mediaType || ''}
                onChange={(e) =>
                  handleFilterChange('mediaType', e.target.value || undefined)
                }
                className="flex h-10 w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-outfront-purple focus:border-transparent"
              >
                <option value="">All Media Types</option>
                <option value="digital">Digital</option>
                <option value="traditional">Traditional</option>
                <option value="streaming">Streaming</option>
                <option value="social">Social</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-600 mb-1">
                Business Category
              </label>
              <select
                value={filters.businessCategory || ''}
                onChange={(e) =>
                  handleFilterChange('businessCategory', e.target.value || undefined)
                }
                className="flex h-10 w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-outfront-purple focus:border-transparent"
              >
                <option value="">All Categories</option>
                <option value="movie_studio">Movie Studio</option>
                <option value="standard">Standard</option>
                <option value="premium">Premium</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-600 mb-1">
                Expiring Within (Days)
              </label>
              <input
                type="number"
                min="0"
                value={filters.expiringWithinDays || ''}
                onChange={(e) =>
                  handleFilterChange(
                    'expiringWithinDays',
                    e.target.value ? parseInt(e.target.value) : undefined
                  )
                }
                placeholder="Days..."
                className="flex h-10 w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-outfront-purple focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-600 mb-1">
                Sort By
              </label>
              <select
                value={filters.sortBy || 'expiration'}
                onChange={(e) =>
                  handleFilterChange('sortBy', e.target.value)
                }
                className="flex h-10 w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-outfront-purple focus:border-transparent"
              >
                <option value="expiration">Expiration Date</option>
                <option value="created">Created Date</option>
                <option value="region">Region</option>
                <option value="status">Status</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-600 mb-1">
                Sort Order
              </label>
              <select
                value={filters.sortOrder || 'asc'}
                onChange={(e) =>
                  handleFilterChange('sortOrder', e.target.value)
                }
                className="flex h-10 w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-outfront-purple focus:border-transparent"
              >
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm text-neutral-600 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.includeExpired || false}
                onChange={(e) => handleFilterChange('includeExpired', e.target.checked || undefined)}
                className="rounded border-neutral-300 text-outfront-purple focus:ring-outfront-purple"
              />
              Show Overdue (expired holds)
            </label>
            <Button variant="secondary" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Queue Items */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Exception Requests ({pagination?.total || 0})</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-neutral-400">No exception requests found matching your filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider whitespace-nowrap w-[100px]">
                      Request #
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider whitespace-nowrap w-[140px]">
                      Advertiser
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider whitespace-nowrap w-[110px]">
                      Category
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider whitespace-nowrap w-[80px]">
                      Region
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider whitespace-nowrap w-[200px]">
                      Line Item
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-neutral-400 uppercase tracking-wider whitespace-nowrap w-[80px]">
                      Qty / Spots
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider whitespace-nowrap w-[110px]">
                      Expiration
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider whitespace-nowrap w-[120px]">
                      Status
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider whitespace-nowrap w-[110px]">
                      Sales AE
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-neutral-400 uppercase tracking-wider whitespace-nowrap w-[280px]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-neutral-200">
                  {items.map((item: ExceptionQueueItem) => (
                    <tr
                      key={item.id}
                      className="hover:bg-neutral-50 cursor-pointer"
                      onClick={(e) => {
                        const target = e.target as HTMLElement;
                        if (target.closest('button, select, a, [role="menu"], [data-no-row-click]')) return;
                        handleOpenDetailModal(item.id);
                      }}
                    >
                      <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-neutral-900">
                        {item.requestNumber}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm text-neutral-900">
                        <div className="max-w-[130px] truncate" title={item.advertiser}>{item.advertiser}</div>
                        <div className="text-xs text-neutral-400 max-w-[130px] truncate" title={item.salesAeName}>AE: {item.salesAeName}</div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm">
                        {getBusinessCategoryBadge(item.businessCategory)}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm text-neutral-900">
                        {item.region}
                      </td>
                      <td className="px-3 py-3 text-sm text-neutral-900">
                        <div className="max-w-[190px] truncate" title={item.displayName}>{item.displayName}</div>
                        <div className="text-xs text-neutral-400 max-w-[190px] truncate">
                          {item.market} • {item.mediaType}
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-right text-sm text-neutral-900">
                        {item.qty ?? 1} / {item.digitalOnlyNoOfSpots != null ? item.digitalOnlyNoOfSpots : '—'}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm">
                        <div>{new Date(item.holdExpirationDate).toLocaleDateString('en-US', { timeZone: 'UTC' })}</div>
                        <div className={`text-xs ${getExpirationColor(item.daysUntilExpiration)}`}>
                          {item.daysUntilExpiration !== undefined &&
                            (item.daysUntilExpiration < 0
                              ? `${Math.abs(item.daysUntilExpiration)}d overdue`
                              : item.daysUntilExpiration === 0
                              ? 'Today'
                              : `${item.daysUntilExpiration}d left`)}
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <Badge className={getStatusBadgeColor(item.exceptionStatus)}>
                          {getStatusText(item.exceptionStatus)}
                        </Badge>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm text-neutral-900">
                        <div className="max-w-[100px] truncate" title={item.salesAeName}>{item.salesAeName}</div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm font-medium">
                        {item.exceptionStatus === 'in_review' ? (
                          <div className="flex gap-1.5">
                            <Button
                              size="sm"
                              onClick={() => handleOpenActionModal(item, 'approve')}
                              disabled={approveException.isPending}
                              className="text-xs px-2.5 py-1"
                            >
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => handleOpenActionModal(item, 'reject')}
                              disabled={rejectException.isPending}
                              className="text-xs px-2.5 py-1"
                            >
                              Reject
                            </Button>
                          </div>
                        ) : (
                          <span className="text-neutral-400 italic text-xs">No actions</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination && pagination.total > (filters.limit || 50) && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-neutral-600">
                Showing {(filters.offset || 0) + 1} to{' '}
                {Math.min((filters.offset || 0) + (filters.limit || 50), pagination.total)} of{' '}
                {pagination.total} results
              </div>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    handleFilterChange('offset', Math.max(0, (filters.offset || 0) - (filters.limit || 50)))
                  }
                  disabled={(filters.offset || 0) === 0}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    handleFilterChange('offset', (filters.offset || 0) + (filters.limit || 50))
                  }
                  disabled={!pagination.hasMore}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Modal */}
      <Modal isOpen={showActionModal && !!selectedItem} onClose={() => { setShowActionModal(false); setSelectedItem(null); setComments(''); }} size="lg">
        {selectedItem && (
          <div className="bg-white rounded-lg p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              {actionType === 'approve'
                ? 'Approve Exception'
                : 'Reject Exception'}
            </h3>
            <div className="mb-4 space-y-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-neutral-600">Request:</span>{' '}
                  <span className="text-neutral-900">{selectedItem.requestNumber}</span>
                </div>
                <div>
                  <span className="font-medium text-neutral-600">Advertiser:</span>{' '}
                  <span className="text-neutral-900">{selectedItem.advertiser}</span>
                </div>
                <div>
                  <span className="font-medium text-neutral-600">Region:</span>{' '}
                  <span className="text-neutral-900">{selectedItem.region}</span>
                </div>
                <div>
                  <span className="font-medium text-neutral-600">Sales AE:</span>{' '}
                  <span className="text-neutral-900">{selectedItem.salesAeName}</span>
                </div>
              </div>
              
              <div className="border-t pt-3">
                <p className="text-sm font-medium text-neutral-600 mb-1">Line Item:</p>
                <p className="text-sm text-neutral-900">{selectedItem.displayName}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-neutral-600">Hold Dates:</span>{' '}
                  <span className="text-neutral-900">
                    {new Date(selectedItem.holdStartDate).toLocaleDateString('en-US', { timeZone: 'UTC' })} -{' '}
                    {new Date(selectedItem.holdExpirationDate).toLocaleDateString('en-US', { timeZone: 'UTC' })}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-neutral-600">Flight Dates:</span>{' '}
                  <span className="text-neutral-900">
                    {new Date(selectedItem.flightStartDate).toLocaleDateString('en-US', { timeZone: 'UTC' })} -{' '}
                    {new Date(selectedItem.flightEndDate).toLocaleDateString('en-US', { timeZone: 'UTC' })}
                  </span>
                </div>
              </div>

              <div className="border-t pt-3">
                <label className="block text-sm font-medium text-neutral-600 mb-2">
                  {actionType === 'approve'
                    ? 'Approval Comments (required)'
                    : 'Reason (required)'}
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-outfront-purple"
                  rows={4}
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder={
                    actionType === 'approve'
                      ? 'Explain why this exception is approved...'
                      : 'Explain why this exception is rejected...'
                  }
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowActionModal(false);
                  setSelectedItem(null);
                  setComments('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitAction}
                disabled={
                  approveException.isPending ||
                  rejectException.isPending
                }
              >
                {actionType === 'approve'
                  ? 'Approve Exception'
                  : 'Reject Exception'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Line Item Detail Modal */}
      {showDetailModal && selectedLineItemId && (
        <LineItemDetail
          lineItemId={selectedLineItemId}
          onClose={handleCloseDetailModal}
          onNavigateToLineItem={(lineItemId) => {
            setSelectedLineItemId(lineItemId);
          }}
          isModal={true}
        />
      )}
    </div>
  );
}
